export const listProduceColumns = ({
  variables = {},
  nodes = {},
  artboards = {},
}) => {
  const used = {};
  const walk = (map) => {
    Object.values(map || {}).forEach((node) => {
      const key =
        node?.source?.kind === "data"
          ? String(node.source.variable || "").trim()
          : "";
      if (key && !used[key]) {
        used[key] = {
          key,
          label: node.name || variables[key]?.label || key,
          type:
            variables[key]?.type === "image" || node.type === "image"
              ? "image"
              : "string",
        };
      }
      if (node.children) walk(node.children);
    });
  };
  Object.values(artboards || {}).forEach((artboard) => {
    walk(artboard?.nodes || {});
  });
  walk(nodes);
  return Object.values(used).sort((left, right) =>
    left.key.localeCompare(right.key)
  );
};

export const createEmptyRow = ({ columns = [] }) => {
  const row = {};
  columns.forEach((column) => {
    row[column.key] = "";
  });
  return row;
};

export const isImageHttpUrl = ({ value }) =>
  /^https?:\/\//i.test(String(value || "").trim());

export const isCellFilled = ({ value, type }) => {
  const text = String(value || "").trim();
  if (!text) return false;
  if (type === "image") return isImageHttpUrl({ value: text });
  return true;
};

export const isRowEmpty = ({ row = {}, columns = [] }) =>
  columns.every(
    (column) => !String(row?.[column.key] || "").trim()
  );

export const hasNonEmptyRows = ({ rows = [], columns = [] }) =>
  rows.some((row) => !isRowEmpty({ row, columns }));

export const getInvalidRowIndexes = ({ rows = [], columns = [] }) =>
  rows
    .map((row, index) => {
      const incomplete = columns.some(
        (column) =>
          !isCellFilled({ value: row?.[column.key], type: column.type })
      );
      return incomplete ? index : -1;
    })
    .filter((index) => index >= 0);

export const getDefaultProduceLanguage = ({ language } = {}) => {
  const code = String(language || "es").split("-")[0];
  if (code === "en" || code === "es" || code === "pt") return code;
  return "es";
};

export const scrollToProduceRow = ({ rowRefs, index }) => {
  const node = rowRefs?.current?.[index];
  if (!node || typeof node.scrollIntoView !== "function") return;
  node.scrollIntoView({ behavior: "smooth", block: "center" });
};

export const normalizeStoredRows = ({ rows } = {}) => {
  if (Array.isArray(rows)) return rows;
  if (rows && typeof rows === "object") {
    return Object.keys(rows)
      .sort((left, right) => Number(left) - Number(right))
      .map((key) => rows[key])
      .filter((row) => row && typeof row === "object");
  }
  return [];
};

export const normalizeStoredIDs = ({ value } = {}) => {
  if (Array.isArray(value)) {
    return value.map((id) => String(id || "").trim()).filter(Boolean);
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort((left, right) => Number(left) - Number(right))
      .map((key) => String(value[key] || "").trim())
      .filter(Boolean);
  }
  return [];
};

export const resolveSelectedArtboardIDs = ({
  availableIDs = [],
  selectedIDs = [],
  storedIDs = [],
} = {}) => {
  if (!availableIDs.length) return [];
  const preferred = storedIDs.length > 0 ? storedIDs : selectedIDs;
  const preferredSet = new Set(preferred);
  const next = availableIDs.filter((id) => preferredSet.has(id));
  return next.length > 0 ? next : [...availableIDs];
};

export const toggleSelectedID = ({
  availableIDs = [],
  selectedIDs = [],
  id,
} = {}) => {
  if (!id || !availableIDs.includes(id)) return selectedIDs;
  const selectedSet = new Set(selectedIDs);
  if (selectedSet.has(id)) {
    if (selectedSet.size <= 1) return selectedIDs;
    selectedSet.delete(id);
  } else {
    selectedSet.add(id);
  }
  return availableIDs.filter((availableID) => selectedSet.has(availableID));
};

export const listProduceCreatives = ({ creatives } = {}) =>
  Object.values(creatives || {})
    .filter((item) => item?.url)
    .sort(
      (left, right) =>
        (left.rowIndex ?? 0) - (right.rowIndex ?? 0) ||
        String(left.artboardID || "").localeCompare(String(right.artboardID || "")) ||
        (left.creationTime ?? 0) - (right.creationTime ?? 0)
    )
    .map((item) => ({
      id: item.id,
      url: item.url,
      type: item.type || "upload",
      width: item.width,
      height: item.height,
      artboardID: item.artboardID || null,
      rowIndex: item.rowIndex,
      instance: item.instance || null,
      creationTime: item.creationTime,
    }));
